import { useEffect, useState } from "react";
import GridPage from "components/layout/GridPage";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TextField from "components/common/TextField";
import Loading from "components/common/Loading";
import { useListAuditRecords, useListAuditRecordTypes } from "services/admin";
import {
  AuditRecord,
  ListAuditRecordsRequest,
} from "protogen/admin_service_pb";
import AdvisorSelect from "components/creation/AdvisorSelect";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import DateDisplay from "components/common/DateDisplay";
import useIsMobile from "components/hooks/useIsMobile";

const escapeValue = (value: string): string => {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const convertToCSV = (records: AuditRecord[]): string => {
  const header = [
    "Timestamp",
    "Event Type",
    "Username",
    "Description",
    "Context",
  ];
  const rows = [
    header.map(escapeValue).join(","),
    ...records.map((record) =>
      [
        new Date(Number(record.timestampSec) * 1000).toISOString(),
        record.eventType,
        record.actorName,
        record.description,
        record.contextJson,
      ]
        .map((v) => escapeValue(v || ""))
        .join(","),
    ),
  ];
  return rows.join("\r\n");
};

const downloadCSV = (records: AuditRecord[]) => {
  const blob = new Blob([convertToCSV(records)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const today = new Date();
  const yy = String(today.getFullYear()).slice(-2);
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  link.setAttribute("href", url);
  link.setAttribute("download", `AuditLog.${yy}${mm}${dd}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default () => {
  const isMobile = useIsMobile();
  const [modalData, setModalData] = useState<string | null>(null);
  const [advisorRef, setAdvisorRef] = useState<string>("");
  const [familyRef, setFamilyRef] = useState<string>("");
  const [eventType, setEventType] = useState<string>("");
  const { data, loading, request } = useListAuditRecords();
  const { data: typesData, request: requestTypes } = useListAuditRecordTypes();

  useEffect(() => {
    requestTypes();
  }, []);

  useEffect(() => {
    request(new ListAuditRecordsRequest({ advisorRef, familyRef, eventType }));
  }, [advisorRef, familyRef, eventType]);

  const eventTypes = typesData?.eventTypes || [];
  const hasFilters = !!(advisorRef || familyRef || eventType);

  return (
    <GridPage
      sx={{
        padding: isMobile ? "32px 24px" : "48px",
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
      }}
    >
      <Typography variant="title" id="tableTitle">
        Audit Log
      </Typography>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap="12px"
        alignItems={isMobile ? "stretch" : "center"}
        mb="16px"
      >
        <Box flex={1} minWidth="200px">
          <AdvisorSelect
            isAdmin
            clearable
            title="Advisor"
            selectedAdvisor={advisorRef || null}
            onChange={(ref) => setAdvisorRef(ref || "")}
          />
        </Box>
        <Box flex={1} minWidth="200px">
          <AdminFamilySelect
            title="Family"
            selectedFamily={familyRef || null}
            onChange={(ref) => setFamilyRef(ref || "")}
          />
        </Box>
        <Box flexShrink={0} width={isMobile ? "100%" : "200px"}>
          <FormControl sx={{ width: "100%" }} fullWidth>
            <Autocomplete
              value={eventType || null}
              onChange={(_, value) => setEventType(value || "")}
              options={eventTypes}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event Type"
                  sx={{
                    ".MuiInputBase-root": {
                      paddingTop: "3px",
                      paddingBottom: "3px",
                      cursor: "pointer",
                    },
                    "& .MuiInputBase-input": {
                      cursor: "pointer",
                      paddingLeft: "11px",
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Box>
        {hasFilters && (
          <Button
            variant="text"
            onClick={() => {
              setAdvisorRef("");
              setFamilyRef("");
              setEventType("");
            }}
          >
            Clear
          </Button>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="audit log table">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              (data?.records || []).map((record) => (
                <TableRow
                  key={record.ref}
                  hover
                  onClick={() => setModalData(record.contextJson)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <DateDisplay
                      date={new Date(Number(record.timestampSec) * 1000)}
                    />
                  </TableCell>
                  <TableCell>{record.eventType}</TableCell>
                  <TableCell>{record.actorName}</TableCell>
                  <TableCell>{record.description}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {loading && <Loading />}
      {!loading && !!(data?.records || []).length && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="start"
          gap="16px"
          mt="24px"
          width="100%"
        >
          <Button onClick={() => downloadCSV(data?.records || [])}>
            Download
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              navigator.clipboard.writeText(convertToCSV(data?.records || []))
            }
          >
            Copy to Clipboard
          </Button>
        </Box>
      )}
      <Modal
        open={modalData !== null}
        onClose={() => setModalData(null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Context
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{ mt: 2, whiteSpace: "pre" }}
          >
            {modalData}
          </Typography>
        </Box>
      </Modal>
    </GridPage>
  );
};
